import { ChangeDetectionStrategy, Component, inject, model, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { ButtonComponent } from '../button/button.component';
import { AuthService } from '../../../../core/auth/auth.service';
import { NotificationService } from '../../../../core/services/notification.service';

/**
 * Hallmark · component: change-password dialog · genre: modern-minimal
 * states: hidden · default · focus · busy · error · success
 * contrast: pass
 */
@Component({
  selector: 'app-change-password-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, ButtonComponent],
  template: `
    @if (open()) {
      <div class="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" role="dialog" aria-modal="true">
        <div class="w-full max-w-sm rounded-2xl border border-[var(--color-surface-border)] bg-[var(--color-surface-raised)] p-6 shadow-2xl">
          <h3 class="text-base font-semibold text-[var(--color-text-primary)]">Ubah Kata Sandi</h3>
          <p class="mt-1 text-sm text-[var(--color-text-muted)]">Masukkan kata sandi saat ini dan yang baru (min. 8 karakter).</p>

          <div class="mt-4 flex flex-col gap-3">
            <label class="flex flex-col gap-1">
              <span class="text-xs text-[var(--color-text-muted)]">Kata sandi saat ini</span>
              <input type="password" autocomplete="current-password" [(ngModel)]="current"
                class="rounded-lg border border-[var(--color-surface-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text-primary)] focus:border-[var(--color-brand-500)] focus:outline-none" />
            </label>
            <label class="flex flex-col gap-1">
              <span class="text-xs text-[var(--color-text-muted)]">Kata sandi baru</span>
              <input type="password" autocomplete="new-password" [(ngModel)]="next"
                class="rounded-lg border border-[var(--color-surface-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text-primary)] focus:border-[var(--color-brand-500)] focus:outline-none" />
            </label>
            <label class="flex flex-col gap-1">
              <span class="text-xs text-[var(--color-text-muted)]">Ulangi kata sandi baru</span>
              <input type="password" autocomplete="new-password" [(ngModel)]="confirm"
                class="rounded-lg border border-[var(--color-surface-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text-primary)] focus:border-[var(--color-brand-500)] focus:outline-none" />
            </label>
          </div>

          @if (error()) {
            <p class="mt-3 rounded-lg border border-[var(--color-danger)]/30 bg-[var(--color-danger)]/10 px-3 py-2 text-xs text-[var(--color-danger)]">{{ error() }}</p>
          }

          <div class="mt-5 flex justify-end gap-2">
            <app-button variant="ghost" size="md" (click)="close()">Batal</app-button>
            <app-button variant="primary" size="md" [loading]="saving()" (click)="submit()">Simpan</app-button>
          </div>
        </div>
      </div>
    }
  `,
})
export class ChangePasswordDialogComponent {
  private auth = inject(AuthService);
  private notif = inject(NotificationService);

  readonly open = model(false);

  current = '';
  next = '';
  confirm = '';
  readonly saving = signal(false);
  readonly error = signal('');

  close(): void {
    this.open.set(false);
    this.current = this.next = this.confirm = '';
    this.error.set('');
  }

  async submit(): Promise<void> {
    this.error.set('');
    if (this.next.length < 8) { this.error.set('Kata sandi baru minimal 8 karakter.'); return; }
    if (this.next !== this.confirm) { this.error.set('Ulangan kata sandi tidak cocok.'); return; }
    this.saving.set(true);
    try {
      await this.auth.changePassword(this.current, this.next);
      this.saving.set(false);
      this.notif.push('success', 'Kata sandi diubah', 'Gunakan kata sandi baru pada login berikutnya.');
      this.close();
    } catch (e: any) {
      this.saving.set(false);
      this.error.set(e?.error?.detail ?? 'Gagal mengubah kata sandi.');
    }
  }
}
